import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Confetti from 'react-confetti';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ContentContainer from '../components/contentContainer';
import WidgetDialog from '../components/dialogs';
import TitleHeader from '../components/titleHeader';
import useShareMenu from '../components/share/useShareMenu';
import ShareMenu from '../components/share';
import PrimaryButton from '../components/primaryButton';
import { useStyles } from './styles';

import useWidgetTheme from '../../commons/widgetTheme';
import TranslationIds from '../../commons/translationIds';
import useTranslator from '../../commons/translations';
import getCommonUIStyles from '../../commons/uiStyles';
import Recommedations from './recommendations';
import useRecommendations from './useRecommendations';
import useElementSize from '../../commons/hooks/useElementSize';
import useUIStates from '../../store/models/uistates/useUIStates';
import { getCalendarName } from '../../commons/utils/utils';

import usePageTransitions from '../../store/models/animations/usePageTransitions';
import {
    TransitionViewNames,
} from '../../store/models/animations/actions/transitions';
import useKickstarted from '../../commons/hooks/useKickstarted';

function Success (props) {
    const {
        viewAnimation = false,
        initStyles = {},
    } = props;

    const {
        translate,
        translateWithValues,
    } = useTranslator();

    const {
        selectedCalendar,
        selectedCalApp,
        shareMessageUrl,
    } = useUIStates();

    const {
        appStore,
    } = useKickstarted({});

    const {
        widget,
        widgetTheme,
        hasCustomCSS,
    }  = useWidgetTheme();

    const {
        recommendations,
        hasRecommendations,
        getAdUnit,
        shouldRenderAdUnit,
    } = useRecommendations({ widget });

    const {
        shareMenuOpen,
        showShareMenu,
        shareMenuAnchor,
        closeShareMenu,
        shareWidget,
    } = useShareMenu({
        widget,
        shareMessageUrl,
     });

    const {
        size,
        elementRef,
    } = useElementSize({
        selector : `#${TransitionViewNames.SUCCESS_VIEW}`,
    });

    const classes = useStyles({
        widget,
        widgetTheme,
        hasCustomCSS,
        borderColor: widget?.background_button_color,
        commonUIStyles: getCommonUIStyles({ widgetTheme, widget }),
    });

    const renderShare = () => {
        return (
            <React.Fragment>
                <ShareMenu
                    open={shareMenuOpen}
                    anchorEl={shareMenuAnchor}
                    onClose={() => closeShareMenu()}
                    onShare={(target) => {
                        shareWidget(target);
                        closeShareMenu();
                    }}
                />
            </React.Fragment>
        );
    }

    function renderAdUnit () {
        const adUnit = getAdUnit();
        return (
            <Grid className={classes.adSpace}>
                <Grid
                    className={classes.adImage}
                    onClick={() => (window.open(`${adUnit.url}`, '_blank'))}
                >
                    <img
                        className={classes.adUnitImage}
                        src={adUnit.image}
                        alt="Ad Unit"
                        /> 
                </Grid>
            </Grid>
        );
    }

    function renderRecommendations () {
        if (hasRecommendations) {
            return (<Recommedations
                        classes={classes}
                        recommendations={recommendations}
                    />);
        };

        if (!shouldRenderAdUnit()) {
            return (<React.Fragment></React.Fragment>);
        }
        return renderAdUnit();
    }

    usePageTransitions({
        elementName: TransitionViewNames.SUCCESS_VIEW,
        viewAnimation,
    });

    function renderShareButton () {
        if (widget.shareDisabled) {
            return (<React.Fragment></React.Fragment>);
        }
        return (<PrimaryButton
            isDisabled={false}
            onClick={(e) => {showShareMenu(e.currentTarget)}}
            textLabel={translateWithValues(TranslationIds.COMPONENT_SHARE)}
        />);
    }
    
    if (!appStore.kickstarted) {
        return (<React.Fragment></React.Fragment>);
    }

    return (
        <React.Fragment>
            <ContentContainer
                viewAnimation={viewAnimation}
            >
                <Grid className={classes.successContainer} ref={elementRef}>
                    <WidgetDialog
                        id={TransitionViewNames.SUCCESS_VIEW}
                        isShowBackButton={false}
                        name={TransitionViewNames.SUCCESS_VIEW}
                        viewAnimation={viewAnimation}
                        initStyles={initStyles}
                    >
                        <Confetti
                            data-ecal-id="success-confetti"
                            width={size?.width}
                            height={size?.height}
                            recycle={false}
                            colors={[' #EF7624', ' #F50057', ' #FEC260', ' #75BD43', ' #00A7E0']}
                        />
                        <TitleHeader
                            widget={widget}
                            widgetTheme={widgetTheme}
                            title={`${translate(TranslationIds.COMPONENT_SUCCESS)}!`}
                            subtitle={''}
                        />
                        <Grid className={classNames([classes.postSubscriptionBody])}>
                            <Grid className={classNames([classes.subtitle])}>
                                <Typography>
                                    {translateWithValues(TranslationIds.SUCCESS_PAGE_SUCCESS_INFO_SUBTITLE, {calapp: selectedCalApp, calappname: getCalendarName(selectedCalendar)})}
                                </Typography>
                            </Grid>
                            <Grid className={classNames([classes.bodyMessages])}>
                                <Typography>
                                    {translate(TranslationIds.SUCCESS_PAGE_SUCCESS_INFO_BODY)}
                                </Typography>
                            </Grid>
                            <Grid className={classNames([classes.shareButtonPanel])}>
                                { renderShareButton() }
                            </Grid>
                            { renderRecommendations() }
                        </Grid>
                        {renderShare()}
                    </WidgetDialog>
                </Grid>
            </ContentContainer>           
        </React.Fragment>
    );
}

Success.propTypes = {
    viewAnimation: PropTypes.bool,
    initStyles: PropTypes.any,
};

export default observer(Success);